import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./auth";
import { ConfirmProvider } from "./components/ConfirmProvider";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import MnsLanding from "./pages/MnsLanding";
import { Login, Register, AuthCallback } from "./pages/Auth";
import MapView from "./pages/MapView";
import PoolDetail from "./pages/PoolDetail";
import MnsDetail from "./pages/MnsDetail";
import { ClientProfile, MnsProfileEdit, PaymentSuccess, PaymentCancel } from "./pages/ClientPages";
import ClientDashboard from "./pages/ClientDashboard";
import Profile from "./pages/Profile";
import Favorites from "./pages/Favorites";
import { Inbox, Conversation } from "./pages/Messages";
import MnsDashboard from "./pages/MnsDashboard";
import AdminPanel from "./pages/AdminPanel";
import CartPage from "./pages/CartPage";
import LegalPage from "./pages/LegalPage";
import AppReviews from "./pages/AppReviews";
import ContactPage from "./pages/ContactPage";
import NotificationsPage from "./pages/NotificationsPage";
import CarnetClient from "./pages/CarnetClient";
import UserPublicCard from "./pages/UserPublicCard";
import HelpPage from "./pages/HelpPage";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12 text-center text-slate-500">Chargement...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronously detect oauth callback to prevent race conditions
  if (location.hash?.includes("session_id=")) {
    return <Layout><AuthCallback /></Layout>;
  }
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/landing/mns" element={<MnsLanding />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/map" element={<MapView />} />
        <Route path="/pool/:id" element={<PoolDetail />} />
        <Route path="/mns/:id" element={<MnsDetail />} />
        <Route path="/profile/client" element={<Protected roles={["client"]}><ClientProfile /></Protected>} />
        <Route path="/profile" element={<Protected roles={["client", "mns", "admin"]}><Profile /></Protected>} />
        <Route path="/favorites" element={<Protected roles={["client"]}><Favorites /></Protected>} />
        <Route path="/messages" element={<Protected roles={["client", "mns", "admin"]}><Inbox /></Protected>} />
        <Route path="/messages/:id" element={<Protected roles={["client", "mns", "admin"]}><Conversation /></Protected>} />
        <Route path="/dashboard" element={<Protected roles={["client", "mns", "admin"]}>
          {/* dispatch by role */}
          <RoleRedirect />
        </Protected>} />
        <Route path="/mns/profile" element={<Protected roles={["mns"]}><MnsProfileEdit /></Protected>} />
        <Route path="/mns/dashboard" element={<Protected roles={["mns"]}><MnsDashboard /></Protected>} />
        <Route path="/admin" element={<Protected roles={["admin"]}><AdminPanel /></Protected>} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/cart" element={<Protected roles={["client"]}><CartPage /></Protected>} />
        <Route path="/legal/:doc" element={<LegalPage />} />
        <Route path="/avis" element={<AppReviews />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/aide" element={<HelpPage />} />
        <Route path="/notifications" element={<Protected roles={["client", "mns", "admin"]}><NotificationsPage /></Protected>} />
        <Route path="/carnet" element={<Protected roles={["client"]}><CarnetClient /></Protected>} />
        <Route path="/u/:id" element={<Protected roles={["client", "mns", "admin"]}><UserPublicCard /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function RoleRedirect() {
  const { user } = useAuth();
  if (user?.role === "mns") return <Navigate to="/mns/dashboard" replace />;
  if (user?.role === "admin") return <Navigate to="/admin" replace />;
  return <ClientDashboard />;
}

export default function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
  );
}
